import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import useScrollReveal from "../hooks/useScrollReveal";
import { useLanguage } from "../i18n/LanguageContext";

export default function BlogPreview() {
  const [ref, isVisible] = useScrollReveal();
  const { t } = useLanguage();
  const articles = t.blog.articles.slice(0, 3);
  const bp = t.blogPreview;

  return (
    <section ref={ref} data-testid="blog-preview-section" className="bg-[#0F1A2E] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
          className="flex items-end justify-between mb-14"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.7s ease-out",
          }}
        >
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-12 bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs uppercase tracking-widest">{t.blog.label}</span>
            </div>
            <h2
              className="text-3xl md:text-4xl text-white"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {bp.heading}
            </h2>
          </div>
          <Link
            to="/blog"
            data-testid="blog-preview-view-all"
            className="hidden md:flex items-center gap-2 text-[#D4AF37] text-sm uppercase tracking-wider hover:text-[#F59E0B] transition-colors group"
          >
            {bp.viewAll}
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {articles.map((article, i) => (
            <Link
              key={article.slug}
              to={`/blog/${article.slug}`}
              data-testid={`blog-preview-card-${i}`}
              className="group card-hover-lift bg-[#162A45] border border-slate-800 overflow-hidden block"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(24px)",
                transition: "opacity 0.7s ease-out, transform 0.7s ease-out, box-shadow 0.4s, border-color 0.5s",
                transitionDelay: `${i * 120}ms`,
              }}
            >
              <div className="h-48 overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <span className="text-[#D4AF37] text-[10px] uppercase tracking-widest font-medium">
                  {article.category}
                </span>
                <h3
                  className="text-white text-base mt-2 mb-3 leading-snug group-hover:text-[#D4AF37] transition-colors duration-300"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {article.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed line-clamp-2">{article.excerpt}</p>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-800/60">
                  <span className="text-slate-500 text-xs">{article.date}</span>
                  <span className="text-slate-500 text-xs">{article.readTime}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <Link
          to="/blog"
          className="md:hidden flex items-center justify-center gap-2 mt-8 text-[#D4AF37] text-sm uppercase tracking-wider hover:text-[#F59E0B] transition-colors"
        >
          {bp.viewAll}
          <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
}
